import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { safeBlobGet } from '../utils/fetchPdfBlob';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  CaretLeft, CaretRight, ArrowsOutSimple, ArrowsInSimple,
  X, Buildings, FileText, ClipboardText,
  DeviceMobile, Lightning, ChartLineUp, ShieldCheck,
  ArrowRight, CheckCircle, Target, Gear, Users,
  Clock, Eye, CaretDown, CaretUp, Cursor, Info,
  PlusCircle, MagicWand, SealCheck, FilePdf, FileDoc,
  ArrowsClockwise, Microphone, Camera, Globe
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ========== SLIDE DATA ==========

const SLIDES = [
  {
    id: 'intro',
    layout: 'cover',
    title: 'CHANTIX',
    subtitle: 'Votre assistant de gestion de chantier',
    tagline: 'Solution métier pour professionnels OPC & CSPS',
    accent: 'Simplifiez. Automatisez. Professionnalisez.',
  },
  {
    id: 'problematique',
    layout: 'two-col',
    title: 'La problematique terrain',
    subtitle: 'Ce que vivent les professionnels chaque jour',
    left: {
      heading: 'Avant CHANTIX',
      items: [
        'Documents rediges manuellement, un par un',
        'Ressaisie des memes informations dans chaque rapport',
        'Pas de suivi centralise des observations',
        'Versions de documents qui se perdent',
        'Difficile de travailler sur le terrain avec un PC',
      ],
    },
    right: {
      heading: 'Avec CHANTIX',
      items: [
        'Documents générés automatiquement depuis les donnees chantier',
        'Source unique : saisissez une fois, reutilisez partout',
        'Registre alimente automatiquement par les PV de visite',
        'Versioning et verrouillage integres',
        'Interface mobile-first, utilisable sur chantier',
      ],
    },
  },
  {
    id: 'fonctionnalites',
    layout: 'interactive-grid',
    title: 'Fonctionnalites principales',
    subtitle: 'Cliquez sur chaque fonction pour decouvrir son utilite',
    items: [
      {
        icon: 'buildings', label: 'Créer un chantier',
        btnLabel: 'Nouveau chantier',
        desc: 'Fiches completes avec intervenants, risques, planning',
        tutorial: 'Ce bouton ouvre le formulaire de création. Renseignez le maître d\'ouvrage, les entreprises, les risques identifiés et le planning prévisionnel. Ces données serviront de source unique pour tous vos documents.',
        when: 'A utiliser : des le demarrage d\'une nouvelle mission OPC ou CSPS.',
      },
      {
        icon: 'filetext', label: 'Documents CSPS',
        btnLabel: 'Générer un document',
        desc: 'PGC, PPSPS, DIUO, ICP générés automatiquement',
        tutorial: 'Sélectionnez le type de document et le chantier. Le système généré un template complet pre-rempli avec toutes les donnees du chantier : intervenants, risques, contraintes, planning.',
        when: 'A utiliser : lors de la phase de preparation ou a chaque mise a jour documentaire.',
      },
      {
        icon: 'clipboard', label: 'Rapports terrain',
        btnLabel: 'Nouveau rapport',
        desc: 'PV de visite, Registre des observations, Rapport mensuel',
        tutorial: 'Creez des PV directement apres chaque visite. Les observations sont automatiquement centralisees dans le Registre. Le rapport mensuel synthetise l\'ensemble de la période.',
        when: 'A utiliser : apres chaque visite de chantier ou en fin de mois.',
      },
      {
        icon: 'shield', label: 'Suivi sécurité',
        btnLabel: 'Ajouter une observation',
        desc: 'Observations, non-conformites, actions correctives',
        tutorial: 'Saisissez vos observations avec la localisation, la gravite, l\'entreprise responsable et l\'action corrective demandee. Le suivi est automatique : ouvert, en cours, leve.',
        when: 'A utiliser : des qu\'un ecart de sécurité est constate sur le chantier.',
      },
      {
        icon: 'gear', label: 'Workflow complet',
        btnLabel: 'Valider et exporter',
        desc: 'Creation, edition par section, validation, export PDF & Word',
        tutorial: 'Editez chaque section individuellement. Une fois satisfait, validez le document pour le verrouiller. Seuls les documents valides peuvent etre exportes en PDF ou Word professionnel.',
        when: 'A utiliser : quand le document est pret a etre diffuse aux intervenants.',
      },
      {
        icon: 'users', label: 'Mode OPC / CSPS',
        btnLabel: 'Changer de mode',
        desc: 'Basculez entre OPC et CSPS dans la meme application',
        tutorial: 'Un bouton en haut de la sidebar permet de basculer entre mode OPC (réserves, planning, rapports) et mode CSPS (documents reglementaires, visites, registre). Vos donnees sont separees mais accessibles au meme endroit.',
        when: 'A utiliser : si vous exercez les deux missions sur des chantiers differents.',
      },
    ],
  },
  {
    id: 'workflow',
    layout: 'interactive-steps',
    title: 'Votre workflow au quotidien',
    subtitle: 'Cliquez sur chaque etape pour voir comment faire',
    steps: [
      {
        num: '1', icon: 'buildings',
        label: 'Créer le chantier',
        desc: 'Renseignez les donnees cles du chantier',
        btnLabel: 'Nouveau chantier',
        btnIcon: 'plus',
        tutorial: 'Dans le menu "Chantiers", cliquez sur "Nouveau chantier". Remplissez les 4 sections : Travaux (nature, duree), Organisation (MO, MOE, entreprises), Sécurité (risques, contraintes), Administratif (references). Ces informations alimenteront automatiquement tous vos documents.',
        result: 'Resultat : fiche chantier complete, prete a générér des documents.',
      },
      {
        num: '2', icon: 'filetext',
        label: 'Générer les documents',
        desc: 'Un template pre-rempli en un clic',
        btnLabel: 'Générer le template',
        btnIcon: 'magic',
        tutorial: 'Dans "Documents CSPS" ou "Rapports", cliquez sur "Nouveau". Choisissez le type (PGC, PPSPS, DIUO, ICP, PV...) et le chantier. Le système généré instantanement un document structure avec toutes les donnees du chantier injectees dans les bonnes sections.',
        result: 'Resultat : document pre-rempli avec les donnees du chantier.',
      },
      {
        num: '3', icon: 'gear',
        label: 'Editer et enrichir',
        desc: 'Modifiéz section par section',
        btnLabel: 'Completer le document',
        btnIcon: 'sparkle',
        tutorial: 'Ouvrez le document, chaque section est repliable. Editez le texte directement. Utilisez le bouton "Completer" pour enrichir automatiquement les parties techniques. Vous pouvez aussi regénérér une section specifique sans toucher aux autres.',
        result: 'Resultat : document finalise avec contenu technique professionnel.',
      },
      {
        num: '4', icon: 'check',
        label: 'Valider et exporter',
        desc: 'Verrouillez puis exportez',
        btnLabel: 'Valider le document',
        btnIcon: 'seal',
        tutorial: 'Cliquez sur "Valider" : le document est verrouille et sa version incrementee (V1.0, V2.0...). Seul un document valide peut etre exporte en PDF ou Word. Si besoin, creez une nouvelle version pour modifiér a nouveau.',
        result: 'Resultat : document officiel verrouille, exportable et tracable.',
      },
      {
        num: '5', icon: 'eye',
        label: 'Suivre sur le terrain',
        desc: 'PV et observations en temps reel',
        btnLabel: 'Importer les observations',
        btnIcon: 'sync',
        tutorial: 'Apres chaque visite, creez un PV avec les observations constatees. Dans le Registre, cliquez sur "Importer PV" : toutes les observations de vos PV sont automatiquement centralisees. Plus de ressaisie, plus d\'oubli.',
        result: 'Resultat : registre a jour, rapport mensuel pre-alimente.',
      },
    ],
  },
  {
    id: 'mobile',
    layout: 'interactive-features',
    title: 'Concu pour le terrain',
    subtitle: 'Decouvrez les fonctions terrain en cliquant sur chaque element',
    features: [
      {
        icon: 'mobile', label: 'Navigation tactile',
        btnLabel: 'Barre de navigation',
        tutorial: 'En bas de l\'ecran, une barre de navigation donne acces en un tap aux sections principales : Chantiers, Documents, Rapports, Visites, Profil. Les boutons sont dimensionnes pour le doigt, meme avec des gants.',
      },
      {
        icon: 'lightning', label: 'Saisie rapide',
        btnLabel: 'Formulaire adapte',
        tutorial: 'Les formulaires utilisent des listes deroulantes, des boutons de selection rapide et des champs pre-remplis. Sur mobile, les zones de saisie sont agrandies et les actions principales toujours accessibles sans scroller.',
      },
      {
        icon: 'microphone', label: 'Notes vocales',
        btnLabel: 'Enregistrér une note',
        tutorial: 'Appuyez sur le micro pour dicter vos observations. La transcription est automatique. Idéal pour consigner rapidement une observation sur le chantier sans avoir à taper au clavier.',
      },
      {
        icon: 'camera', label: 'Photos chantier',
        btnLabel: 'Prendre une photo',
        tutorial: 'Depuis une réserve ou une observation, prenez une photo directement. Elle est rattâchee a l\'element concerne et disponible dans le rapport. Plus besoin de transferer des photos manuellement.',
      },
      {
        icon: 'globe', label: 'Acces universel',
        btnLabel: 'Ouvrir dans le navigateur',
        tutorial: 'CHANTIX fonctionne dans le navigateur (Chrome, Safari, Firefox). Pas d\'application a installer, pas de mise a jour a gerer. Ouvrez l\'URL, connectez-vous, travaillez. Les documents exportes (PDF, Word) sont consultables hors-ligne.',
      },
    ],
    highlight: 'Aucune installation requise. Fonctionne sur tous les navigateurs.',
  },
  {
    id: 'avantages',
    layout: 'interactive-cards',
    title: 'Les avantages concrets',
    subtitle: 'Cliquez pour voir des exemples chiffres',
    cards: [
      {
        icon: 'lightning', title: 'Gain de temps', color: 'orange',
        desc: 'Reduisez le temps de redaction documentaire',
        example: 'Exemple : un PGC de 15 pages necessite habituellement 3 a 4 heures de redaction. Avec CHANTIX, le template est généré en 30 secondes. L\'enrichissement et les ajustements prennent environ 20 minutes. Soit un gain de 80% du temps.',
      },
      {
        icon: 'eye', title: 'Tracabilite', color: 'blue',
        desc: 'Historique complet et documents verrouilles',
        example: 'Exemple : un PGC passe par 3 versions (V1.0, V2.0, V3.0). Chaque version est datee, signee numeriquement et conservee. En cas de contrôle, vous retrouvez instantanement la version en vigueur a une date donnee.',
      },
      {
        icon: 'shield', title: 'Conformite', color: 'green',
        desc: 'Templates conformes aux normes BTP',
        example: 'Exemple : les templates CSPS suivent les articles R.4532-13 et suivants du Code du travail. Les sections obligatoires (analyse des risques, mesures de prevention, organisation des secours) sont systematiquement presentes.',
      },
      {
        icon: 'target', title: 'Zero ressaisie', color: 'purple',
        desc: 'Saisissez une fois, reutilisez partout',
        example: 'Exemple : les coordonnees du maitre d\'ouvrage, les entreprises et les risques identifies sont saisis une seule fois dans la fiche chantier. Ils apparaissent automatiquement dans le PGC, le PPSPS, les PV et le registre.',
      },
    ],
  },
  {
    id: 'conclusion',
    layout: 'conclusion',
    title: 'Pret a simplifier votre quotidien ?',
    subtitle: 'CHANTIX vous accompagne du premier coup de pioche au dernier document',
    points: [
      'Solution complete OPC + CSPS',
      'Documents professionnels en quelques clics',
      'Utilisable directement sur le chantier',
      'En evolution constante avec vos retours',
    ],
    cta: 'Demarrons ensemble',
  },
];

// ========== ICON HELPER ==========

function getIcon(name, size = 32, className = '') {
  const props = { size, className, weight: 'bold' };
  const map = {
    buildings: <Buildings {...props} />, filetext: <FileText {...props} />,
    clipboard: <ClipboardText {...props} />, shield: <ShieldCheck {...props} />,
    gear: <Gear {...props} />, users: <Users {...props} />,
    mobile: <DeviceMobile {...props} />, lightning: <Lightning {...props} />,
    clock: <Clock {...props} />, target: <Target {...props} />,
    chart: <ChartLineUp {...props} />, eye: <Eye {...props} />,
    check: <CheckCircle {...props} />, plus: <PlusCircle {...props} />,
    magic: <MagicWand {...props} />, sparkle: <MagicWand {...props} />,
    seal: <SealCheck {...props} />, sync: <ArrowsClockwise {...props} />,
    pdf: <FilePdf {...props} />, doc: <FileDoc {...props} />,
    microphone: <Microphone {...props} />, camera: <Camera {...props} />,
    globe: <Globe {...props} />,
  };
  return map[name] || <FileText {...props} />;
}

// ========== INTERACTIVE BUTTON ==========

function DemoButton({ label, icon, isOpen, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`group inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
        isOpen
          ? 'bg-[#FF6B00] text-white shadow-md shadow-orange-200'
          : 'bg-[#FF6B00]/10 text-[#FF6B00] hover:bg-[#FF6B00] hover:text-white hover:shadow-md hover:shadow-orange-200'
      }`}>
      {icon && getIcon(icon, 16, isOpen ? 'text-white' : 'text-[#FF6B00] group-hover:text-white')}
      <Cursor size={14} className={`${isOpen ? 'text-white/70' : 'text-[#FF6B00]/50 group-hover:text-white/70'}`} weight="fill" />
      {label}
      {isOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
    </button>
  );
}

function TutorialBox({ children, result, when }) {
  return (
    <div className="mt-3 bg-white border border-[#FF6B00]/20 rounded-xl p-4 shadow-sm animate-in slide-in-from-top-2 duration-200">
      <div className="flex items-start gap-2.5">
        <Info size={18} className="text-[#FF6B00] flex-shrink-0 mt-0.5" weight="fill" />
        <div className="space-y-2">
          <p className="text-sm text-[#374151] leading-relaxed">{children}</p>
          {result && (
            <p className="text-xs font-semibold text-emerald-600 bg-emerald-50 rounded-lg px-3 py-1.5 inline-block">
              {result}
            </p>
          )}
          {when && (
            <p className="text-xs text-[#FF6B00] font-medium italic">{when}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ========== SLIDE COMPONENTS ==========

function CoverSlide({ slide }) {
  return (
    <div className="flex flex-col items-center justify-center h-full w-full text-center px-8">
      <div className="relative">
        <div className="absolute -inset-8 bg-[#FF6B00]/5 rounded-full blur-3xl" />
        <h1 className="relative text-7xl sm:text-8xl lg:text-9xl font-black tracking-tight text-[#FF6B00]" data-testid="slide-cover-title">
          {slide.title}
        </h1>
      </div>
      <p className="text-xl sm:text-2xl text-[#374151] mt-6 max-w-2xl font-light">{slide.subtitle}</p>
      <p className="text-base sm:text-lg text-[#6B7280] mt-3">{slide.tagline}</p>
      <div className="mt-10 px-6 py-3 rounded-full bg-[#FF6B00]/10 text-[#FF6B00] font-semibold text-sm sm:text-base tracking-wide">
        {slide.accent}
      </div>
    </div>
  );
}

function TwoColSlide({ slide }) {
  return (
    <div className="flex flex-col h-full w-full px-6 sm:px-12 py-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#111827]">{slide.title}</h2>
        <p className="text-base sm:text-lg text-[#6B7280] mt-2">{slide.subtitle}</p>
      </div>
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
        <div className="bg-[#FEF2F2] rounded-2xl p-6 sm:p-8 border border-red-100">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
              <X size={24} className="text-red-500" weight="bold" />
            </div>
            <h3 className="text-lg font-bold text-red-700">{slide.left.heading}</h3>
          </div>
          <ul className="space-y-3">
            {slide.left.items.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-red-800/80">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />{item}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-[#F0FDF4] rounded-2xl p-6 sm:p-8 border border-green-100">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
              <CheckCircle size={24} className="text-green-600" weight="bold" />
            </div>
            <h3 className="text-lg font-bold text-green-700">{slide.right.heading}</h3>
          </div>
          <ul className="space-y-3">
            {slide.right.items.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-green-800/80">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0" />{item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function InteractiveGridSlide({ slide }) {
  const [openIdx, setOpenIdx] = useState(null);
  const toggle = (i) => setOpenIdx(openIdx === i ? null : i);
  return (
    <div className="flex flex-col h-full w-full px-6 sm:px-10 py-6 overflow-y-auto">
      <div className="text-center mb-5">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#111827]">{slide.title}</h2>
        <p className="text-sm sm:text-base text-[#6B7280] mt-1">{slide.subtitle}</p>
      </div>
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 content-start">
        {slide.items.map((item, i) => (
          <div key={item.title || `feature-${i}`} className={`bg-white rounded-xl border transition-all duration-200 ${
            openIdx === i ? 'border-[#FF6B00]/40 shadow-md' : 'border-gray-100 shadow-sm'
          }`} data-testid={`feature-card-${i}`}>
            <div className="p-4 sm:p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#FF6B00]/10 flex items-center justify-center flex-shrink-0">
                  {getIcon(item.icon, 22, 'text-[#FF6B00]')}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-sm text-[#111827]">{item.label}</h3>
                  <p className="text-xs text-[#6B7280] mt-0.5">{item.desc}</p>
                </div>
              </div>
              <DemoButton label={item.btnLabel} icon={item.icon} isOpen={openIdx === i}
                onClick={() => toggle(i)} testId={`demo-btn-${i}`} />
              {openIdx === i && (
                <TutorialBox when={item.when}>{item.tutorial}</TutorialBox>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InteractiveStepsSlide({ slide }) {
  const [openIdx, setOpenIdx] = useState(null);
  const toggle = (i) => setOpenIdx(openIdx === i ? null : i);
  return (
    <div className="flex flex-col h-full w-full px-6 sm:px-10 py-6 overflow-y-auto">
      <div className="text-center mb-5">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#111827]">{slide.title}</h2>
        <p className="text-sm sm:text-base text-[#6B7280] mt-1">{slide.subtitle}</p>
      </div>
      <div className="flex-1 flex flex-col max-w-3xl mx-auto w-full">
        {slide.steps.map((step, i) => (
          <div key={step.title || `step-${i}`} className="flex items-start gap-4" data-testid={`step-item-${i}`}>
            <div className="flex flex-col items-center">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 transition-colors ${
                openIdx === i ? 'bg-[#FF6B00] text-white ring-4 ring-[#FF6B00]/20' : 'bg-[#FF6B00] text-white'
              }`}>
                {step.num}
              </div>
              {i < slide.steps.length - 1 && <div className="w-0.5 flex-1 min-h-[16px] bg-[#FF6B00]/20" />}
            </div>
            <div className="pb-4 flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {getIcon(step.icon, 18, 'text-[#FF6B00]')}
                <h3 className="font-bold text-base text-[#111827]">{step.label}</h3>
              </div>
              <p className="text-sm text-[#6B7280] mb-2">{step.desc}</p>
              <DemoButton label={step.btnLabel} icon={step.btnIcon} isOpen={openIdx === i}
                onClick={() => toggle(i)} testId={`step-btn-${i}`} />
              {openIdx === i && (
                <TutorialBox result={step.result}>{step.tutorial}</TutorialBox>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InteractiveFeaturesSlide({ slide }) {
  const [openIdx, setOpenIdx] = useState(null);
  const toggle = (i) => setOpenIdx(openIdx === i ? null : i);
  return (
    <div className="flex flex-col h-full w-full px-6 sm:px-10 py-6 overflow-y-auto">
      <div className="text-center mb-5">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#111827]">{slide.title}</h2>
        <p className="text-sm sm:text-base text-[#6B7280] mt-1">{slide.subtitle}</p>
      </div>
      <div className="flex-1 flex flex-col justify-start max-w-2xl mx-auto w-full space-y-3">
        {slide.features.map((feat, i) => (
          <div key={feat.label || `feature-${i}`} className={`bg-white rounded-xl border transition-all duration-200 ${
            openIdx === i ? 'border-[#FF6B00]/40 shadow-md' : 'border-gray-100 shadow-sm'
          }`} data-testid={`mobile-feat-${i}`}>
            <div className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-[#FF6B00]/10 flex items-center justify-center flex-shrink-0">
                    {getIcon(feat.icon, 22, 'text-[#FF6B00]')}
                  </div>
                  <h3 className="font-bold text-sm text-[#111827]">{feat.label}</h3>
                </div>
                <DemoButton label={feat.btnLabel} icon={feat.icon} isOpen={openIdx === i}
                  onClick={() => toggle(i)} testId={`mobile-btn-${i}`} />
              </div>
              {openIdx === i && (
                <TutorialBox>{feat.tutorial}</TutorialBox>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 text-center">
        <span className="inline-block px-5 py-2 rounded-full bg-[#FF6B00]/10 text-[#FF6B00] font-semibold text-xs">
          {slide.highlight}
        </span>
      </div>
    </div>
  );
}

function InteractiveCardsSlide({ slide }) {
  const [openIdx, setOpenIdx] = useState(null);
  const toggle = (i) => setOpenIdx(openIdx === i ? null : i);
  const colorMap = {
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', activeBorder: 'border-[#FF6B00]', icon: 'text-[#FF6B00]', title: 'text-orange-800' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', activeBorder: 'border-blue-500', icon: 'text-blue-600', title: 'text-blue-800' },
    green: { bg: 'bg-green-50', border: 'border-green-200', activeBorder: 'border-green-500', icon: 'text-green-600', title: 'text-green-800' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', activeBorder: 'border-purple-500', icon: 'text-purple-600', title: 'text-purple-800' },
  };
  return (
    <div className="flex flex-col h-full w-full px-6 sm:px-10 py-6 overflow-y-auto">
      <div className="text-center mb-5">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#111827]">{slide.title}</h2>
        <p className="text-sm sm:text-base text-[#6B7280] mt-1">{slide.subtitle}</p>
      </div>
      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4 content-start max-w-3xl mx-auto w-full">
        {slide.cards.map((card, i) => {
          const c = colorMap[card.color] || colorMap.orange;
          return (
            <div key={card.title || `advantage-${i}`} className={`${c.bg} border rounded-2xl p-5 transition-all duration-200 cursor-pointer ${
              openIdx === i ? `${c.activeBorder} shadow-md` : c.border
            }`}
            onClick={() => toggle(i)}
            data-testid={`advantage-card-${i}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-xl bg-white/80 flex items-center justify-center ${c.icon}`}>
                  {getIcon(card.icon, 22)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-bold text-base ${c.title}`}>{card.title}</h3>
                  <p className="text-xs text-[#4B5563]">{card.desc}</p>
                </div>
                <div className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  openIdx === i ? 'bg-white/80 text-[#374151]' : `${c.icon} bg-white/60`
                }`}>
                  {openIdx === i ? <CaretUp size={14} /> : 'Voir'}
                </div>
              </div>
              {openIdx === i && (
                <div className="mt-3 bg-white/80 rounded-lg p-3 border border-white">
                  <div className="flex items-start gap-2">
                    <ChartLineUp size={16} className={`${c.icon} flex-shrink-0 mt-0.5`} weight="bold" />
                    <p className="text-xs text-[#374151] leading-relaxed">{card.example}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConclusionSlide({ slide }) {
  return (
    <div className="flex flex-col items-center justify-center h-full w-full text-center px-8">
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111827] max-w-2xl">{slide.title}</h2>
      <p className="text-base sm:text-lg text-[#6B7280] mt-4 max-w-xl">{slide.subtitle}</p>
      <div className="mt-8 space-y-3 text-left">
        {slide.points.map((pt, i) => (
          <div key={`check-${i}`} className="flex items-center gap-3">
            <CheckCircle size={22} className="text-[#FF6B00] flex-shrink-0" weight="fill" />
            <span className="text-base sm:text-lg text-[#374151] font-medium">{pt}</span>
          </div>
        ))}
      </div>
      <div className="mt-10">
        <div className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#FF6B00] text-white font-bold text-lg shadow-lg shadow-orange-200">
          <ArrowRight size={22} weight="bold" />
          {slide.cta}
        </div>
      </div>
      <p className="mt-8 text-sm text-[#9CA3AF]">CHANTIX — Solution metier BTP</p>
    </div>
  );
}

function renderSlide(slide) {
  switch (slide.layout) {
    case 'cover': return <CoverSlide slide={slide} />;
    case 'two-col': return <TwoColSlide slide={slide} />;
    case 'interactive-grid': return <InteractiveGridSlide slide={slide} />;
    case 'interactive-steps': return <InteractiveStepsSlide slide={slide} />;
    case 'interactive-features': return <InteractiveFeaturesSlide slide={slide} />;
    case 'interactive-cards': return <InteractiveCardsSlide slide={slide} />;
    case 'conclusion': return <ConclusionSlide slide={slide} />;
    default: return null;
  }
}

// ========== MAIN SLIDESHOW ==========

export function SlideshowPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [accessGranted, setAccessGranted] = useState(false);
  const [checking, setChecking] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [showDownloads, setShowDownloads] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/presentation/slideshow/access`, { withCredentials: true })
      .then(() => setAccessGranted(true))
      .catch(() => {
        setAccessGranted(false);
        navigate('/dashboard', { replace: true });
      })
      .finally(() => setChecking(false));
  }, [navigate, setAccessGranted, setChecking]);

  const next = useCallback(() => setCurrent(c => Math.min(c + 1, SLIDES.length - 1)), []);
  const prev = useCallback(() => setCurrent(c => Math.max(c - 1, 0)), []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, [containerRef]);

  const download = async (version, format) => {
    setDownloading(`${version}-${format}`);
    try {
      const res = await safeBlobGet(`${API}/presentation/${version}/${format}?personalized=true`);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const vLabel = version === 'short' ? 'Synthetique' : 'Complete';
      a.download = `CHANTIX_Presentation_${vLabel}.${format}`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { window.URL.revokeObjectURL(url); }, 10000);
      try { document.body.removeChild(a); } catch (_e) { void _e; }
      toast.success(`Presentation ${vLabel} (${format.toUpperCase()}) telechargee`);
    } catch (err) {
      toast.error(err.message || 'Erreur lors du téléchargement');
    } finally {
      setDownloading(null);
      setShowDownloads(false);
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      if (e.key === 'Escape') { if (document.fullscreenElement) document.exitFullscreen(); }
      if (e.key === 'f' || e.key === 'F') toggleFullscreen();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [next, prev, toggleFullscreen]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, [setIsFullscreen]);

  if (checking) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#FAFAFA]">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
      </div>
    );
  }

  if (!accessGranted) return null;

  const slide = SLIDES[current];
  const progress = ((current + 1) / SLIDES.length) * 100;

  return (
    <div ref={containerRef} className="h-screen flex flex-col bg-[#FAFAFA] select-none overflow-hidden"
      data-testid="slideshow-page">

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white border-b border-gray-100 z-10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}
            className="h-9 w-9 text-gray-500 hover:text-gray-800" data-testid="slideshow-close-btn">
            <X size={20} />
          </Button>
          <span className="text-sm font-semibold text-[#111827]">CHANTIX</span>
          <span className="text-xs text-[#9CA3AF] hidden sm:inline">Presentation interactive</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-[#6B7280] tabular-nums" data-testid="slide-counter">
            {current + 1} / {SLIDES.length}
          </span>
          {/* Download dropdown */}
          <div className="relative">
            <Button variant="ghost" size="icon" onClick={() => setShowDownloads(!showDownloads)}
              className="h-9 w-9 text-gray-500 hover:text-[#FF6B00]" data-testid="slideshow-download-btn">
              <FilePdf size={20} />
            </Button>
            {showDownloads && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1"
                data-testid="slideshow-download-menu">
                <p className="px-3 py-1.5 text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Télécharger</p>
                {[
                  { version: 'short', label: 'Synthetique (2p)', formats: ['pdf', 'docx'] },
                  { version: 'full', label: 'Complete (5-8p)', formats: ['pdf', 'docx'] },
                ].map(({ version, label, formats }) => (
                  <div key={version}>
                    <p className="px-3 py-1 text-xs font-semibold text-[#374151]">{label}</p>
                    {formats.map(fmt => (
                      <button key={`${version}-${fmt}`}
                        onClick={() => handleDownload(version, fmt)}
                        disabled={!!downloading}
                        className="w-full px-3 py-2 text-left text-xs text-[#4B5563] hover:bg-orange-50 hover:text-[#FF6B00] flex items-center gap-2 transition-colors"
                        data-testid={`download-${version}-${fmt}`}>
                        {downloading === `${version}-${fmt}` ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : fmt === 'pdf' ? (
                          <FilePdf size={14} weight="fill" className="text-red-500" />
                        ) : (
                          <FileDoc size={14} weight="fill" className="text-blue-500" />
                        )}
                        {fmt.toUpperCase()}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={toggleFullscreen}
            className="h-9 w-9 text-gray-500 hover:text-gray-800" data-testid="slideshow-fullscreen-btn">
            {isFullscreen ? <ArrowsInSimple size={20} /> : <ArrowsOutSimple size={20} />}
          </Button>
        </div>
      </div>

      {/* Progress */}
      <div className="h-1 bg-gray-100 flex-shrink-0">
        <div className="h-full bg-[#FF6B00] transition-all duration-300 ease-out" style={{ width: `${progress}%` }}
          data-testid="slideshow-progress" />
      </div>

      {/* Slide */}
      <div className="flex-1 relative overflow-hidden" data-testid="slide-content">
        <div className="absolute inset-0 flex">
          {renderSlide(slide)}
        </div>
        <button className="absolute left-0 top-0 w-[15%] h-full cursor-w-resize opacity-0 pointer-events-none" onClick={prev}
          aria-label="Precedent" disabled={current === 0} data-testid="slide-touch-prev" />
        <button className="absolute right-0 top-0 w-[15%] h-full cursor-e-resize opacity-0 pointer-events-none" onClick={next}
          aria-label="Suivant" disabled={current === SLIDES.length - 1} data-testid="slide-touch-next" />
      </div>

      {/* Bottom */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white border-t border-gray-100 flex-shrink-0">
        <Button variant="outline" onClick={prev} disabled={current === 0}
          className="h-11 px-5 gap-2 text-sm" data-testid="slideshow-prev-btn">
          <CaretLeft size={18} weight="bold" /> Precedent
        </Button>
        <div className="hidden sm:flex items-center gap-1.5" data-testid="slide-dots">
          {SLIDES.map((_, i) => (
            <button key={`dot-${i}`} onClick={() => setCurrent(i)}
              className={`rounded-full transition-all duration-200 ${
                i === current ? 'w-6 h-2 bg-[#FF6B00]' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Slide ${i + 1}`} data-testid={`slide-dot-${i}`} />
          ))}
        </div>
        <Button onClick={current === SLIDES.length - 1 ? () => navigate(-1) : next}
          className="h-11 px-5 gap-2 text-sm bg-[#FF6B00] hover:bg-orange-700"
          data-testid="slideshow-next-btn">
          {current === SLIDES.length - 1 ? 'Terminér' : 'Suivant'} <CaretRight size={18} weight="bold" />
        </Button>
      </div>

      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 text-[10px] text-[#D1D5DB] pointer-events-none hidden lg:block">
        Fleches pour naviguer &middot; F plein ecran &middot; Esc quitter
      </div>
    </div>
  );
}
